import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Pin, FolderOpen, Users } from "lucide-react"
import { ColorPicker } from '@/components/ColorPicker'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

export function ProjectListItem({ item, onColorChange, onPin, onClick }) {
  const [isHovered, setIsHovered] = useState(false)
  const navigate = useNavigate()
  
  const handleColorChange = (color) => {
    onColorChange?.(item.id, color)
  }
  
  const handlePinClick = (e) => {
    e.stopPropagation()
    onPin?.(item)
  }
  
  const handleClick = () => {
    if (item.type === 'stack') {
      onClick?.(item)
    } else {
      navigate(`/space/${item.id}`)
    }
  }
  
  const isStack = item.type === 'stack'
  
  return (
    <div 
      className="group flex items-center gap-4 p-4 hover:bg-muted/50 rounded-lg transition-colors cursor-pointer"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Color indicator */}
      <div 
        className="w-2 h-12 rounded-full flex-shrink-0"
        style={{ backgroundColor: item.color?.value || '#e5e7eb' }}
      />
      
      {/* Icon/Avatar */}
      <div className="flex-shrink-0">
        {isStack ? (
          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
            <FolderOpen className="w-5 h-5 text-muted-foreground" />
          </div>
        ) : (
          <Avatar className="h-10 w-10">
            <AvatarImage
              src={item.avatar || item.teamMembers?.[0]?.avatar}
              alt={item.name}
            />
            <AvatarFallback>{item.name.charAt(0)}</AvatarFallback>
          </Avatar>
        )}
      </div>
      
      {/* Name and details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium truncate">{item.name}</h3>
          {item.isPinned && (
            <Pin className="w-3 h-3 text-blue-600 fill-blue-600" />
          )}
          {item.stackName && (
            <Badge variant="secondary" className="text-xs px-2 py-0">
              <FolderOpen className="w-3 h-3 mr-1" />
              {item.stackName}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground truncate">
          {isStack 
            ? `${item.projects?.length || 0} space${(item.projects?.length || 0) !== 1 ? 's' : ''}`
            : (item.description || item.type || 'Project')
          }
        </p>
      </div>
      
      {/* Team members or stack projects count */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {isStack ? (
          <Badge variant="secondary" className="gap-1">
            <Users className="w-3 h-3" />
            {item.projects?.length || 0}
          </Badge>
        ) : (
          item.teamMembers && item.teamMembers.length > 0 && (
            <div className="flex items-center -space-x-2">
              {item.teamMembers.slice(0, 3).map((member, index) => (
                <Avatar key={member.id || index} className="h-6 w-6 border-2 border-background">
                  <AvatarImage
                    src={member.avatar}
                    alt={member.name}
                  />
                  <AvatarFallback className="text-xs">
                    {member.name ? member.name.charAt(0).toUpperCase() : 'U'}
                  </AvatarFallback>
                </Avatar>
              ))}
              {item.teamMembers.length > 3 && (
                <span className="text-xs text-muted-foreground ml-2">
                  +{item.teamMembers.length - 3}
                </span>
              )}
            </div>
          )
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <div onClick={(e) => e.stopPropagation()}>
          <ColorPicker 
            onColorChange={handleColorChange}
            currentColor={item.color}
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={handlePinClick}
        >
          <Pin className={`h-4 w-4 ${item.isPinned ? 'text-blue-600 fill-blue-600' : 'text-muted-foreground'}`} />
        </Button>
      </div>
    </div>
  )
}